import { useCallback, useState } from 'react';
import clsx from 'clsx';
import {
  Badge,
  Button,
  Separator,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { CopyIcon, ListChecksIcon, PlusIcon, XIcon } from 'lucide-react';
import {
  useApprovalChains,
  type ApprovalChain,
  type DeleteApprovalChainResult,
} from '@/entities/approval-chain';
import { useIsProjectPermissionsResolving, useProjectCapabilities } from '@/entities/user';
import { notificationService } from '@/lib/services/notification.service';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { EmptyState } from '@/shared/ui/EmptyState';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { useDeleteApprovalChain } from '../../api/useDeleteApprovalChain.mutation';
import { useDuplicateApprovalChain } from '../../api/useDuplicateApprovalChain.mutation';
import { CopyApprovalChainDialog } from '../CopyApprovalChainDialog';
import type { ApprovalChainEditorTarget } from '../ApprovalChainEditorPanel';
import { ApprovalChainActionMenu } from './ApprovalChainActionMenu';
import { useTranslation } from 'react-i18next';

export interface ApprovalChainListPanelProps {
  projectId: string;
  /** Shuts the whole drawer. */
  onClose: () => void;
  /** Editing, creating and copying all switch the drawer to its editor subview. */
  onOpenEditor: (target: ApprovalChainEditorTarget) => void;
}

const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_90px_80px_60px] items-center gap-sm';

/**
 * Every project's approval chains: the drawer's first subview, opened from the
 * project actions menu. A row switches the drawer to the editor.
 */
export function ApprovalChainListPanel({
  projectId,
  onClose,
  onOpenEditor,
}: ApprovalChainListPanelProps) {
  const { t } = useTranslation('approvalChains');
  const { t: tCommon } = useTranslation('common');

  const { canManageApprovalChains: canManage } = useProjectCapabilities();
  const isResolvingPermissions = useIsProjectPermissionsResolving();

  const { data, isLoading } = useApprovalChains(projectId);
  const approvalChains = data?.items ?? [];

  const deleteApprovalChain = useDeleteApprovalChain({ projectId });
  const duplicateApprovalChain = useDuplicateApprovalChain({ projectId });

  const [approvalChainPendingDelete, setApprovalChainPendingDelete] =
    useState<ApprovalChain | null>(null);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);

  const handleEdit = useCallback(
    (approvalChain: ApprovalChain) => onOpenEditor({ approvalChainId: approvalChain.id }),
    [onOpenEditor],
  );

  const handleDuplicate = useCallback(
    (approvalChain: ApprovalChain) => {
      duplicateApprovalChain.mutate(approvalChain.id, {
        onSuccess: (created) => {
          notificationService.showSuccess(
            t('manageApprovalChains.approvalChainListPanel.duplicatedAs', {
              sourceName: approvalChain.name,
              newName: created.name,
            }),
          );
        },
        onError: (error) => {
          const { detail } = parseProblemDetail(
            error,
            t('manageApprovalChains.approvalChainListPanel.failedToDuplicateApprovalChain'),
          );
          notificationService.showError(detail);
        },
      });
    },
    [duplicateApprovalChain, t],
  );

  const handleConfirmDelete = () => {
    if (!approvalChainPendingDelete) return;
    const approvalChain = approvalChainPendingDelete;
    deleteApprovalChain.mutate(approvalChain.id, {
      onSuccess: ({ affectedAssetCount }: DeleteApprovalChainResult) => {
        setApprovalChainPendingDelete(null);
        notificationService.showSuccess(
          affectedAssetCount > 0
            ? t('manageApprovalChains.approvalChainListPanel.deletedAssetsReverted', {
                approvalChainName: approvalChain.name,
                count: affectedAssetCount,
              })
            : t('manageApprovalChains.approvalChainListPanel.deleted', {
                approvalChainName: approvalChain.name,
              }),
        );
      },
      onError: (error) => {
        const { detail } = parseProblemDetail(
          error,
          t('manageApprovalChains.approvalChainListPanel.failedToDeleteApprovalChain'),
        );
        notificationService.showError(detail);
      },
    });
  };

  const isEmpty = !isLoading && approvalChains.length === 0;

  return (
    <TooltipProvider>
      <div className="flex h-full min-h-0 flex-col" data-testid="approval-chain-list-panel">
        <div className="flex shrink-0 items-start justify-between gap-md pb-md">
          <div className="min-w-0">
            <h2 className="text-lg font-medium">
              {t('manageApprovalChains.approvalChainListPanel.approvalChains')}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t('manageApprovalChains.approvalChainListPanel.browseTheApprovalChains')}
            </p>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="mini"
                className="size-8 shrink-0 p-0"
                onClick={onClose}
                aria-label={t('manageApprovalChains.approvalChainListPanel.closePanel')}
                data-testid="approval-chain-list-panel-close"
              >
                <XIcon className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {t('manageApprovalChains.approvalChainListPanel.closePanel')}
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="flex min-h-0 flex-1 flex-col justify-between overflow-auto">
          {isLoading ? (
            <div className="flex justify-center py-5xl" data-testid="approval-chain-list-loading">
              <Spinner />
            </div>
          ) : isEmpty ? (
            <EmptyState
              size="compact"
              media={<ListChecksIcon className="text-muted-foreground size-6" />}
              className="gap-xs p-5xl md:p-5xl"
              title={t('manageApprovalChains.approvalChainListPanel.noApprovalChainsYet')}
              description={
                canManage || isResolvingPermissions
                  ? t(
                      'manageApprovalChains.approvalChainListPanel.createAnApprovalChainToStandardize',
                    )
                  : t('manageApprovalChains.approvalChainListPanel.noApprovalChainsHaveBeen')
              }
              data-testid="approval-chain-list-empty-state"
            />
          ) : (
            <div className="flex flex-col">
              <div className={`${ROW_GRID} text-foreground-subtle py-2.5 text-xs font-medium`}>
                <span>{t('manageApprovalChains.approvalChainListPanel.name')}</span>
                <span>{t('manageApprovalChains.approvalChainListPanel.steps')}</span>
                <span>{t('manageApprovalChains.approvalChainListPanel.auto')}</span>
                <span />
              </div>
              <Separator />
              <ul className="flex flex-col">
                {approvalChains.map((approvalChain) => (
                  <li key={approvalChain.id} className="border-b border-border-light">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleEdit(approvalChain)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleEdit(approvalChain);
                        }
                      }}
                      className={`${ROW_GRID} cursor-pointer py-sm hover:bg-ghost`}
                      data-testid={`approval-chain-row-${approvalChain.id}`}
                    >
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <p className="truncate text-sm font-medium">{approvalChain.name}</p>
                        {approvalChain.steps && approvalChain.steps.length > 0 && (
                          <p className="text-foreground-subtle truncate text-xs">
                            {approvalChain.steps
                              .map((step) => step.name)
                              .join(
                                t('manageApprovalChains.approvalChainListPanel.stepTrailSeparator'),
                              )}
                          </p>
                        )}
                        <p className="text-foreground-subtle truncate text-xs">
                          {t('manageApprovalChains.approvalChainListPanel.appliedTo', {
                            folders: t('manageApprovalChains.approvalChainListPanel.folderCount', {
                              count: approvalChain.folderUsageCount,
                            }),
                            assets: t('manageApprovalChains.approvalChainListPanel.assetCount', {
                              count: approvalChain.assetUsageCount,
                            }),
                          })}
                        </p>
                      </div>
                      <span className="text-sm">
                        {t('manageApprovalChains.approvalChainListPanel.stepCount', {
                          count: approvalChain.stepCount,
                        })}
                      </span>
                      <span>
                        {/* The new-folders flag only: applying to existing folders is a
                            one-shot backfill on save, not a standing rule. */}
                        {approvalChain.autoApplyToNewFolders && (
                          <Badge variant="outline" size="sm" roundness="full">
                            {t('manageApprovalChains.approvalChainListPanel.on')}
                          </Badge>
                        )}
                      </span>
                      <ApprovalChainActionMenu
                        approvalChain={approvalChain}
                        canManage={canManage}
                        onEdit={handleEdit}
                        onDuplicate={handleDuplicate}
                        onDelete={setApprovalChainPendingDelete}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(canManage || isResolvingPermissions) && (
            <div
              className={clsx(
                'flex shrink-0 items-center justify-end gap-xs',
                isResolvingPermissions && 'cursor-wait',
              )}
            >
              <Button
                variant="secondary"
                disabled={isResolvingPermissions}
                onClick={() => setIsCopyDialogOpen(true)}
                data-testid="copy-approval-chain-from-project-button"
              >
                <CopyIcon className="size-4" />
                {t('manageApprovalChains.approvalChainListPanel.copyFromAnotherProject')}
              </Button>
              <Button
                disabled={isResolvingPermissions}
                onClick={() => onOpenEditor({})}
                data-testid="create-approval-chain-button"
              >
                <PlusIcon className="size-4" />
                {t('manageApprovalChains.approvalChainListPanel.createNewApprovalChain')}
              </Button>
            </div>
          )}
        </div>

        <CopyApprovalChainDialog
          key={
            isCopyDialogOpen
              ? `copy-approval-chain-open-${projectId}`
              : 'copy-approval-chain-closed'
          }
          open={isCopyDialogOpen}
          targetProjectId={projectId}
          onClose={() => setIsCopyDialogOpen(false)}
          onCopied={(prefill, sourceName) =>
            onOpenEditor({ prefill, prefillSourceName: sourceName })
          }
        />

        <DeleteConfirmationModal
          open={!!approvalChainPendingDelete}
          isDeleting={deleteApprovalChain.isPending}
          resourceType={tCommon('components.deleteConfirmationModal.approvalChain')}
          resourceName={approvalChainPendingDelete?.name}
          warningMessage={
            approvalChainPendingDelete
              ? t(
                  'manageApprovalChains.approvalChainListPanel.thisApprovalChainIsCurrentlyAssigned',
                  {
                    count: approvalChainPendingDelete.assetUsageCount,
                  },
                )
              : ''
          }
          onCancel={() => setApprovalChainPendingDelete(null)}
          onConfirmDelete={handleConfirmDelete}
          data-testid="delete-approval-chain-modal"
        />
      </div>
    </TooltipProvider>
  );
}
